import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { getProject, getProjectFlowStatus, updateProjectFlowStep } from '../../../../Requests/ProjectRequests';
import CreateEditProjectSlideover from '../../../../Components/Projects/CreateEditProjectSlideover';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';
import { Card, Title, Text } from '@tremor/react';
import NewAudioFileUploadModal from '../../../../Components/Files/NewAudioFileUploadModal';
import AnalisysCard from '../../../../Components/Files/AnalisysCard';
import ProjectGpt from '../../../../Components/ProjectGpt/ProjectGpt';
import UploadFileModal from '../../../../Components/Text/TextUploadModal';
import UploadVideoFileModal from '../../../../Components/Files/UploadVideoFileModal';
import UploadFileWithTypes from '../../../../Components/NewAnalysisElements/UploadFileWithTypes';
import { CheckIcon, Cog6ToothIcon, DocumentArrowUpIcon, PencilIcon, TagIcon, TrashIcon, UserIcon, XMarkIcon, ChartBarIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import empty_file_cabinet from '../../../../Assets/Project/empty_file_cabinet.png'
import text_coder from '../../../../Assets/TextCoder/text_coder.png'
import no_subjects from '../../../../Assets/Subjects/subject_image.png'
import TextCodeModal from '../../../../Components/TextCoder/TextCodeModal';
import { getTextCodes } from '../../../../Requests/TextCoderRequests';
import TextCodeExcerptsModal from '../../../../Components/TextCoder/TextCodeExcerptsModal';
import { getTeamUuid } from '../../../../utils/cookies';
import TextTranscriptionUploadModal from '../../../../Components/Text/TextTranscriptionModal';
import UpgradedLoader from '../../../../Components/UpgradedLoader';
import { getAiCodes } from '../../../../Requests/AICodeFinder';
import AICodeFinder from '../../../../Components/AICodeFinder/AICodeFinder';
import AICodeView from '../../../../Components/AICodeFinder/AICodeView';
import TranscriptionCodeView from '../../../../Components/TranscriptionCodeFinder.js/TranscriptionCodeView';
import { ShareIcon } from '@heroicons/react/20/solid';
import TextCodeCombinationModal from '../../../../Components/TextCoder/TextCodeCombinationModal';
import CodeCard from '../../../../Components/TextCoder/CodeCard';
import SubjectPropertiesModal from '../../../../Components/Subjects/SubjectPropertiesModal';
import SubjectCreationModal from '../../../../Components/Subjects/SubjectCreationModal';
import { deleteSubject } from '../../../../Requests/SubjectRequests';
import SubjectPublicFormModal from '../../../../Components/Subjects/SubjectPublicFormModal';
import HierarchicalTables from '../../../../Components/Results/HierarchicalTables';
import Codebook from '../../../../Components/Codebook/Codebook';
import CodeFilePresenceMatrix from '../../../../Components/Results/CodeFilePresenceMatrix';
import PolarAreaCenterLabelsChart from '../../../../Components/Results/PolarAreaCenterLabelsChart';
import PolarAreaChartsPerInterview from '../../../../Components/Results/PolarAreaChartsPerInterview';
import ErrorBoundary from '../../../../Components/ErrorBoundary/ErrorBoundary';
import ArticlesTab from '../../../../Components/Articles/ArticlesTab';
import BibliographyTab from '../../../../Components/Articles/BibliographyTab';
import QuestaoPartidaTab from '../../../../Components/Research/QuestaoPartidaTab';
import ResearchGoalsTab from '../../../../Components/Research/ResearchGoalsTab';
import ResearchHypothesesTab from '../../../../Components/Research/ResearchHypothesesTab';
import MetodologiaTab from '../../../../Components/Research/MetodologiaTab';
import CaracterizacaoTab from '../../../../Components/Research/CaracterizacaoTab';
import BeAnalysesTab from '../../../../Components/BehavioralEconomics/BeAnalysesTab';
import TrAnalysesTab from '../../../../Components/TendencyReport/TrAnalysesTab';
import QuestionnaireTab from '../../../../Components/Questionnaire/QuestionnaireTab';
import VideoConferencesList from '../../../../Components/VideoConference/VideoConferencesList';
import ProjectFlowCard from '../../../../Components/Projects/ProjectFlowCard';
import QuestionnaireAnalyticsCard from '../../../../Components/Questionnaire/QuestionnaireAnalyticsCard';
import { getQuestionnaires } from '../../../../Requests/QuestionnaireRequests';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

function ProjectFlow() {
    const { project_uuid } = useParams();
    const { t } = useTranslation();
    const { isCollapsed } = useSidebar();
    const [project, setProject] = useState(null);
    const [projectSlideover, setProjectSlideOver] = useState(null);
    const [uploadAudioModal, setUploadAudioModal] = useState(null);
    const [textCodes, setTextCodes] = useState(null);
    const [questionnaires, setQuestionnaires] = useState([]);

    // Simple local state for current tab, default to 0
    const [currentTab, setCurrentTab] = useState(0);

    // Flow tracking state
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [completedSubsteps, setCompletedSubsteps] = useState([]);
    const [flowLoading, setFlowLoading] = useState(true);
    const [hasAutoNavigated, setHasAutoNavigated] = useState(false);

    // Handle step change and reset tab to a valid one for the new step
    const handleStepChange = (newStep) => {
        setCurrentStep(newStep);
        // Reset to first available tab for the new step
        switch (newStep) {
            case 0: // Prepare - research planning tabs
                setCurrentTab(11); // Default to QuestaoPartida (Research Question)
                break;
            case 1: // Question - subjects, videoconferences, analysis
                setCurrentTab(6); // Default to Subjects
                break;
            case 2: // Analyze - everything else
                setCurrentTab(0); // Default to Analysis
                break;
            case 3: // Report - results and articles
                setCurrentTab(7); // Default to CategoryResults
                break;
            default:
                setCurrentTab(0);
        }
    };

    // Handle step completion toggle
    const handleStepCompletion = async (stepIndex, completed) => {
        try {
            const action = completed ? 'uncomplete' : 'complete';
            const response = await updateProjectFlowStep(project_uuid, stepIndex, action);
            
            if (response.data.success) {
                setCompletedSteps(response.data.completed_steps);
                setCurrentStep(response.data.current_step);
            }
        } catch (error) {
            console.error("Error updating step completion:", error);
        }
    };

    // Handle substep completion tracking
    const markSubstepComplete = (stepIndex, substepIndex) => {
        const substepId = `${stepIndex}.${substepIndex}`;
        setCompletedSubsteps(prev => {
            if (!prev.includes(substepId)) {
                const newCompleted = [...prev, substepId];
                // Persist to localStorage
                localStorage.setItem(`project_${project_uuid}_completed_substeps`, JSON.stringify(newCompleted));
                console.log(`Marked substep ${substepId} as completed`);
                return newCompleted;
            }
            return prev;
        });
    };

    // Remove substep completion (if needed for future use)
    const markSubstepIncomplete = (stepIndex, substepIndex) => {
        const substepId = `${stepIndex}.${substepIndex}`;
        setCompletedSubsteps(prev => {
            const newCompleted = prev.filter(id => id !== substepId);
            // Persist to localStorage
            localStorage.setItem(`project_${project_uuid}_completed_substeps`, JSON.stringify(newCompleted));
            return newCompleted;
        });
    };

    // Load completed substeps from localStorage
    const loadCompletedSubsteps = () => {
        try {
            const stored = localStorage.getItem(`project_${project_uuid}_completed_substeps`);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error loading completed substeps:', error);
            return [];
        }
    };

    // Determine the first uncompleted research step and navigate to it
    const navigateToFirstUncompletedStep = (currentStep, completedSubsteps) => {
        if (currentStep !== 0) return; // Only auto-navigate in Prepare phase
        
        const researchSteps = [
            { substepId: '1.1', tabId: 11, name: 'Research Question' }, // Research Question
            { substepId: '1.2', tabId: 12, name: 'Research Goals' },   // Research Goals  
            { substepId: '1.3', tabId: 15, name: 'Research Hypotheses' }, // Research Hypotheses
            { substepId: '1.4', tabId: 13, name: 'Methodology' },     // Methodology
            { substepId: '1.5', tabId: 14, name: 'Characterization' } // Characterization
        ];

        // Find first uncompleted step
        const firstUncompleted = researchSteps.find(step => !completedSubsteps.includes(step.substepId));
        
        if (firstUncompleted) {
            // Only navigate if we're not already on the target tab (prevent navigation conflicts)
            if (currentTab !== firstUncompleted.tabId) {
                console.log(`Auto-navigating to first uncompleted step: ${firstUncompleted.substepId} (${firstUncompleted.name})`);
                // Add small delay to prevent race conditions with manual navigation
                setTimeout(() => setCurrentTab(firstUncompleted.tabId), 10);
            } else {
                console.log(`Already on target tab ${firstUncompleted.tabId} (${firstUncompleted.name}), skipping navigation`);
            }
        } else {
            // All research steps completed, go to first tab of prepare phase
            if (currentTab !== 11) {
                console.log('All research steps completed, navigating to Research Question');
                setTimeout(() => setCurrentTab(11), 10);
            } else {
                console.log('All research steps completed, already on Research Question tab');
            }
        }
    };

    const navigate = useNavigate();

    useEffect(() => {
        if (!project_uuid) {
            //navigate("/projects")
        } else {
            Promise.all([
                getProject(project_uuid),
                getProjectFlowStatus(project_uuid)
            ]).then(([projectRes, flowRes]) => {
                setProject(projectRes.data);
                const projectCurrentStep = flowRes.data.current_step;
                setCurrentStep(projectCurrentStep);
                setCompletedSteps(flowRes.data.completed_steps);
                
                // Load completed substeps from localStorage
                const loadedSubsteps = loadCompletedSubsteps();
                setCompletedSubsteps(loadedSubsteps);
                
                // Automatically navigate to appropriate tab for current step
                handleStepChange(projectCurrentStep);
                
                // Auto-navigate to first uncompleted research step if in Prepare phase (only on initial load)
                if (projectCurrentStep === 0 && !hasAutoNavigated) {
                    // Use setTimeout to ensure state updates have completed
                    setTimeout(() => {
                        console.log('Initial auto-navigation to first uncompleted step');
                        navigateToFirstUncompletedStep(projectCurrentStep, loadedSubsteps);
                        setHasAutoNavigated(true);
                    }, 100);
                }
                
                setFlowLoading(false);
            }).catch((error) => {
                console.error("Error loading project data:", error);
                setFlowLoading(false);
            });
        }
    }, [project_uuid]);

    // Define conditional tab visibility based on current step
    const getTabVisibility = (tabId) => {
        switch (currentStep) {
            case 0: // Prepare stage - research planning tabs, bibliography and behavioral economics
                return [9, 11, 12, 15, 13, 14, 18, 17].includes(tabId); // + Tendências, Economia Comportamental
            case 1: // Question stage - subjects, videoconferences, questionnaires
                return [6, 10, 16].includes(tabId); // Subjects, VideoConferences, Questionnaires
            case 2: // Analyze stage - analysis first, then everything else except report tabs
                return ![9, 6, 10, 11, 12, 15, 13, 14, 16, 17, 18, 7, 8].includes(tabId); // Analysis (0) and everything except prepare, question, and report stage tabs
            case 3: // Report stage - results and articles
                return [7, 8].includes(tabId); // CategoryResults, Articles
            default:
                return true;
        }
    }

    let tabs = [
        { name: t("DetailProjects.AnalysisTab"), href: '#', current: currentTab === 0, id: 0, visible: getTabVisibility(0) },
        { name: t("DetailProjects.Subjects"), href: '#', current: currentTab === 6, id: 6, visible: getTabVisibility(6) },
        { name: t("DetailProjects.VideoConferences"), href: '#', current: currentTab === 10, id: 10, visible: getTabVisibility(10) },
        { name: "Questionnaires", href: '#', current: currentTab === 16, id: 16, visible: getTabVisibility(16) },
        { name: t("DetailProjects.ProjectGPT"), href: '#', current: currentTab === 1, id: 1, visible: getTabVisibility(1) },
        { name: t("DetailProjects.Project codes"), href: '#', current: currentTab === 2, id: 2, visible: getTabVisibility(2) },
        { name: t("DetailProjects.Coder"), href: '#', current: currentTab === 3, id: 3, visible: getTabVisibility(3) },
        { name: t("AiCodeDiscovery.Title"), href: '#', current: currentTab === 4, id: 4, visible: getTabVisibility(4) },
        { name: t("AiCodeFinder.Title"), href: '#', current: currentTab === 5, id: 5, visible: getTabVisibility(5) },
        { name: t("CategoryResults.Title"), href: '#', current: currentTab === 7, id: 7, visible: getTabVisibility(7) },
        { name: t("Research.QuestaoPartida.Title"), href: '#', current: currentTab === 11, id: 11, visible: getTabVisibility(11) },
        { name: t("Research.ObjectivosHipoteses.Goals.Title"), href: '#', current: currentTab === 12, id: 12, visible: getTabVisibility(12) },
        { name: t("Research.ObjectivosHipoteses.Hypotheses.Title"), href: '#', current: currentTab === 15, id: 15, visible: getTabVisibility(15) },
        { name: t("Research.Methodology.Title"), href: '#', current: currentTab === 13, id: 13, visible: getTabVisibility(13) },
        { name: t("Research.Caracterizacao.Title"), href: '#', current: currentTab === 14, id: 14, visible: getTabVisibility(14) },
        { name: t("DetailProjects.Bibliography"), href: '#', current: currentTab === 9, id: 9, visible: getTabVisibility(9) },
        { name: t("TendencyReport.TabName"), href: '#', current: currentTab === 18, id: 18, visible: getTabVisibility(18) },
        { name: t("BehavioralEconomics.TabName"), href: '#', current: currentTab === 17, id: 17, visible: getTabVisibility(17) },
        { name: t("DetailProjects.Articles"), href: '#', current: currentTab === 8, id: 8, visible: getTabVisibility(8) },
    ]

    const handleNewFileUpload = (type) => {
        if (type === "Audio") {
            setUploadAudioModal(<NewAudioFileUploadModal handleClose={() => { setUploadAudioModal(null); }} project={project} />);
        } else if (type === "Pdf") {
            setUploadAudioModal(<UploadFileModal handleClose={() => { setUploadAudioModal(null); }} />);
        } else if (type == "Transcription") {
            setUploadAudioModal(<TextTranscriptionUploadModal handleClose={() => { setUploadAudioModal(null); }} />);
        } else if (type === "Video") {
            setUploadAudioModal(<UploadVideoFileModal handleClose={() => { setUploadAudioModal(null); }} project={project} />);
        }
    };

    const handleChangeTab = (tabIndex) => {
        if (tabIndex == 3) {
            navigate("/text_coder/" + project_uuid)
        } else {
            // Simple local state update
            setCurrentTab(tabIndex);
        }
    }

    useEffect(() => {
        if (!textCodes) {
            getTextCodes(project_uuid).then((res) => {
                setTextCodes(res.data)
            }).catch((error) => {

            });
        }
    }, [])

    useEffect(() => {
        if (project_uuid) {
            getQuestionnaires(project_uuid).then((res) => {
                setQuestionnaires(res.data ?? []);
            }).catch(() => {});
        }
    }, [project_uuid]);

    function handleUpdateCode(updatedCode) {
        setTextCodes(prevCodes => prevCodes.map(code => code.uuid === updatedCode.uuid ? updatedCode : code));
    }
    function handleAddNewCode(newCode) {
        setTextCodes(prevCodes => [...prevCodes, newCode]);
    }
    function handleCombineCodes(codesToDelete, newCode) {
        setTextCodes(prevCodes => {
            // Filter out the codes to be deleted
            const updatedCodes = prevCodes.filter(code => !codesToDelete.includes(code.uuid));

            // Add the new combined code
            return [...updatedCodes, newCode];
        });
    }

    const formatPropertyValue = (prop) => {
        if (prop.property_definition?.type === 'boolean') {
            return prop.value ? t('Common.Yes') : t('Common.No');
        }
        return prop.value;
    };

    return (
        <div data-testid="project-flow-container" className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 w-full flex justify-center`}>
            <div data-testid="project-flow-content-wrapper" className='flex flex-col w-full justify-center items-center px-4'>
                {/* Navigation Breadcrumb */}
                <div className="mb-6 w-full max-w-7xl">
                    <nav className="flex items-center space-x-2 text-sm text-gray-500">
                        <button
                            onClick={() => navigate('/projects')}
                            className="flex items-center hover:text-gray-700"
                            data-testid="back-to-projects-button"
                        >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            {t('ProjectOverview.AllProjects')}
                        </button>
                        <span>›</span>
                        <button
                            onClick={() => navigate(`/project/${project_uuid}`)}
                            className="text-gray-700 hover:text-iavisus-100"
                            data-testid="back-to-overview-button"
                        >
                            {project?.name || 'Project'}
                        </button>
                        <span>›</span>
                        <span className="text-iavisus-100 font-medium">{t('ProjectOverview.ProjectFlow')}</span>
                    </nav>
                </div>


                <ProjectFlowCard
                    tabs={tabs}
                    currentTab={currentTab}
                    onTabClick={handleChangeTab}
                    currentStep={currentStep}
                    setCurrentStepIndex={handleStepChange}
                    completedSteps={completedSteps}
                    completedSubsteps={completedSubsteps}
                />

                <div data-testid="project-flow-main-card" className='bg-white max-w-7xl rounded-3xl w-full h-full shadow-xl mx-8 mb-8'>
                    <Card data-testid="project-flow-tabs-card">

                        {currentTab == 0 && <div data-testid="project-flow-analysis-tab">
                            {/* Header with icon, title and description */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <ChartBarIcon className="h-6 w-6 text-iavisus-100" />
                                    <Title className="text-lg font-semibold">
                                        {t("DetailProjects.AnalysisTab")}
                                    </Title>
                                </div>
                                <button data-testid="project-flow-upload-file-btn" className='border border-iavisus-100 bg-white h-fit text-iavisus-100 rounded-md px-3 py-2 font-bold text-xs'
                                    type='button'
                                    onClick={() => {
                                        /*setUploadAudioModal(<NewAudioFileUploadModal handleClose={() => { setUploadAudioModal(null) }} project={project} */
                                        setUploadAudioModal(<UploadFileWithTypes handleClose={() => { setUploadAudioModal(null) }} handleNewFileUpload={handleNewFileUpload} />)
                                    }}
                                >
                                    <div className='flex justify-center items-center gap-2 whitespace-nowrap'>
                                        <DocumentArrowUpIcon className='w-4 h-4' />
                                        {t("DetailProjects.UploadFile")}
                                    </div>
                                </button>
                            </div>
                            
                            <Text className="text-gray-600 mb-6">
                                {t("DetailProjects.AnalysisDescription")}
                            </Text>

                            {project?.analysis?.length > 0 ? <div data-testid="project-flow-analysis-grid" className='grid grid-cols-1 lg:grid-cols-2 py-4 justify-evenly gap-12 items-center'>

                                {project?.analysis?.map((current_analysis) => {
                                    return (<AnalisysCard data-testid={`analysis-card-${current_analysis.uuid}`} meeting={current_analysis} key={current_analysis.uuid} />)
                                })}

                            </div> :

                                (project == null || flowLoading ? <UpgradedLoader className="animate-pulse" w={8} h={8} bg="bg-iavisus-100"></UpgradedLoader> :
                                    <div data-testid="project-flow-empty-analysis" className='w-full py-12 mt-4 mb-2 rounded-md bg-white p-4 flex flex-col items-center gap-8 flex-grow'>
                                        <div className='text-xl font-bold'>
                                            {t('DetailProjects.This project has no files yet')}
                                        </div>
                                        <div className='text-base text-gray-500 '>
                                            {t('DetailProjects.Add a file to start')}

                                        </div>
                                        <img src={empty_file_cabinet} className=' h-96 aspect-square mt-2'></img>
                                        <button data-testid="project-flow-empty-upload-btn" className='border border-iavisus-100 bg-iavisus-100 text-white text-xl rounded-md px-4 py-2 '
                                            type='button'
                                            onClick={() => {
                                                /*setUploadAudioModal(<NewAudioFileUploadModal handleClose={() => { setUploadAudioModal(null) }} project={project} */
                                                setUploadAudioModal(<UploadFileWithTypes handleClose={() => { setUploadAudioModal(null) }} handleNewFileUpload={handleNewFileUpload} />)
                                            }}
                                        >
                                            <div className='w-full flex justify-center items-center gap-2 whitespace-nowrap'>
                                                <DocumentArrowUpIcon className='w-6 h-6' />
                                                {t("DetailProjects.UploadFile")}
                                            </div>
                                        </button>
                                    </div>
                                )
                            }

                            {questionnaires.length > 0 && (
                                <div className="mt-8">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="h-px flex-1 bg-gray-100" />
                                        <Text className="text-xs text-gray-400 uppercase tracking-wide px-2">Questionnaires</Text>
                                        <div className="h-px flex-1 bg-gray-100" />
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {questionnaires.map(q => (
                                            <QuestionnaireAnalyticsCard
                                                key={q.uuid}
                                                questionnaire={q}
                                                projectUuid={project_uuid}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>}
                        {currentTab == 1 && <div data-testid="project-flow-gpt-tab">
                            <div data-testid="project-flow-gpt-content" className='grid grid-cols-1  py-4 justify-evenly gap-12 items-center'>
                                <ProjectGpt data-testid="project-flow-gpt-component" project={project} />
                            </div>
                        </div>}
                        {currentTab == 2 && <div data-testid="project-flow-codes-tab">
                            <Codebook data-testid="project-flow-codebook" textCodes={textCodes} setUploadAudioModal={setUploadAudioModal} handleUpdateCode={handleUpdateCode} handleAddNewCode={handleAddNewCode} t={t} />
                        </div>}
                        {currentTab == 4 &&
                            <div data-testid="project-flow-ai-discovery-tab">
                                <AICodeView data-testid="project-flow-ai-code-view" project={project} setUploadAudioModal={setUploadAudioModal} handleUpdateCode={handleUpdateCode} handleAddNewCode={handleAddNewCode} />
                            </div>
                        }
                        {currentTab == 5 &&
                            <div data-testid="project-flow-transcription-code-tab">
                                <TranscriptionCodeView data-testid="project-flow-transcription-code-view" project={project} setUploadAudioModal={setUploadAudioModal} handleUpdateCode={handleUpdateCode} handleAddNewCode={handleAddNewCode} textCodes={textCodes} />
                            </div>
                        }
                        {currentTab == 6 && (
                            <div data-testid="project-flow-subjects-tab" className="mt-4 flex flex-col gap-4">
                                {/* Header with icon, title and description */}
                                <div className="flex items-center space-x-3 mb-4">
                                    <UserGroupIcon className="h-6 w-6 text-iavisus-100" />
                                    <Title className="text-lg font-semibold">
                                        {t("DetailProjects.Subjects")}
                                    </Title>
                                </div>
                                
                                <Text className="text-gray-600 mb-6">
                                    {t("DetailProjects.SubjectsDescription")}
                                </Text>

                                <div>
                                    {project?.subjects?.length > 0 ? (
                                        <div data-testid="project-flow-subjects-grid" className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                            {project.subjects.map(subject => (
                                                <div key={subject.uuid} data-testid={`subject-card-${subject.uuid}`} className="p-4 border rounded-lg">
                                                    <div className="flex gap-4 mb-3">
                                                        <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                                                            <UserIcon className="h-6 w-6 text-gray-500" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex justify-between">
                                                                <h3 className="text-lg font-medium">{subject.name}</h3>
                                                                <div className="flex gap-2">
                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            data-testid={`subject-edit-btn-${subject.uuid}`}
                                                                            onClick={() => setUploadAudioModal(
                                                                                <SubjectCreationModal
                                                                                    handleClose={() => setUploadAudioModal(null)}
                                                                                    project={project}
                                                                                    subject={subject}
                                                                                    t={t}
                                                                                    onSave={(updatedSubject) => {
                                                                                        setProject(prev => ({
                                                                                            ...prev,
                                                                                            subjects: prev.subjects.map(s =>
                                                                                                s.uuid === updatedSubject.uuid ? updatedSubject : s
                                                                                            )
                                                                                        }));
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            className="p-1 text-gray-500 hover:text-gray-700"
                                                                        >
                                                                            <PencilIcon className="h-5 w-5" />
                                                                        </button>
                                                                        <button
                                                                            data-testid={`subject-delete-btn-${subject.uuid}`}
                                                                            onClick={() => {
                                                                                if (window.confirm(t('DetailProjects.DeleteSubjectConfirm'))) {
                                                                                    deleteSubject(subject.uuid);
                                                                                    setProject(prev => ({
                                                                                        ...prev,
                                                                                        subjects: prev.subjects.filter(s => s.uuid !== subject.uuid)
                                                                                    }));
                                                                                }
                                                                            }}
                                                                            className="p-1 text-red-500 hover:text-red-700"
                                                                        >
                                                                            <TrashIcon className="h-5 w-5" />
                                                                        </button>

                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <p className="text-gray-600">{subject.description}</p>
                                                        </div>
                                                    </div>
                                                    {subject.property_values && <div className='text-sm text-gray-400 mt-4'>Properties:</div>}
                                                    <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                                                        {subject.property_values?.map(prop => (
                                                            <div key={prop.uuid}>
                                                                <span className="font-bold">{prop.property_definition?.name}: </span>
                                                                <span>{formatPropertyValue(prop)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className='w-full py-12 mt-4 mb-2 rounded-md bg-white p-4 flex flex-col items-center gap-8'>
                                            <div className='text-xl font-bold'>{t('DetailProjects.No subjects')}</div>
                                            <div className='text-base text-gray-500'>{t('DetailProjects.No subjects submessage')}</div>
                                            <img src={no_subjects} className='h-96 aspect-square mt-2' alt="" />
                                        </div>

                                    )}
                                </div>
                                <div data-testid="project-flow-subjects-actions" className="flex gap-4 mb-4 w-full justify-between">
                                    <button
                                        data-testid="project-flow-create-subject-btn"
                                        className='border border-iavisus-100 bg-iavisus-100 text-white text-sm rounded-md px-4 py-2'
                                        onClick={() => setUploadAudioModal(
                                            <SubjectCreationModal
                                                handleClose={() => setUploadAudioModal(null)}
                                                project={project}
                                                t={t}
                                                onSave={(newSubject) => {
                                                    setProject(prev => ({
                                                        ...prev,
                                                        subjects: [...(prev.subjects || []), {
                                                            ...newSubject,
                                                            property_values: newSubject.property_values.map(pv => ({
                                                                ...pv,
                                                                property_definition: project.property_definitions.find(pd => pd.id === pv.property_definition_id)
                                                            }))
                                                        }]
                                                    }));
                                                }}
                                            />
                                        )}
                                    >
                                        <div className='w-full flex justify-center items-center gap-2 whitespace-nowrap'>
                                            <UserIcon className='w-6 h-6' />
                                            {t("SubjectProperties.Create subject")}
                                        </div>
                                    </button>
                                    <div className='flex gap-4 '>
                                        <button
                                            data-testid="project-flow-subject-properties-btn"
                                            className='border border-iavisus-100 bg-iavisus-100 text-white text-sm rounded-md px-4 py-2'
                                            onClick={() => setUploadAudioModal(
                                                <SubjectPropertiesModal
                                                    handleClose={() => setUploadAudioModal(null)}
                                                    project_uuid={project.uuid}
                                                    project={project}
                                                    onSave={(updatedProject) => setProject(updatedProject)}
                                                />
                                            )}
                                        >
                                            <div className='w-full flex justify-center items-center gap-2 whitespace-nowrap'>
                                                <Cog6ToothIcon className='w-6 h-6' />
                                                {t("SubjectProperties.Create subject properties")}
                                            </div>
                                        </button>
                                        <button
                                            data-testid="project-flow-public-form-btn"
                                            className='border border-iavisus-100 bg-iavisus-100 text-white text-sm rounded-md px-4 py-2'
                                            onClick={() => setUploadAudioModal(
                                                <SubjectPublicFormModal
                                                    handleClose={() => setUploadAudioModal(null)}
                                                    project={project}
                                                    t={t}
                                                />
                                            )}
                                        >
                                            <div className='w-full flex justify-center items-center gap-2 whitespace-nowrap'>
                                                <ShareIcon className='w-6 h-6' />
                                                {t("SubjectProperties.ManagePublicForm")}
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentTab == 7 && (
                            <div data-testid="project-flow-results-tab" className="mt-4 flex flex-col gap-4">
                                {/* Header with icon, title and description */}
                                <div className="flex items-center space-x-3 mb-4">
                                    <ChartBarIcon className="h-6 w-6 text-iavisus-100" />
                                    <Title className="text-lg font-semibold">
                                        {t("CategoryResults.Title")}
                                    </Title>
                                </div>
                                
                                <Text className="text-gray-600 mb-6">
                                    {t("CategoryResults.Description")}
                                </Text>

                                {(textCodes?.some(code => code.excerpts && code.excerpts.length > 0)) ? <div data-testid="project-flow-results-content" className='flex flex-col gap-8'>
                                    <HierarchicalTables data-testid="project-flow-hierarchical-tables" data={textCodes}

                                    />

                                    <CodeFilePresenceMatrix
                                        data-testid="project-flow-presence-matrix"
                                        project={project}
                                        codes={textCodes}
                                        title={t("CategoryResults.InterviewPresenceHeader")}
                                    />
                                    {textCodes && textCodes.length > 0 && project.analysis && project.analysis.length > 0 && (
                                        <>
                                            <ErrorBoundary minimal>
                                                <PolarAreaCenterLabelsChart
                                                    data-testid="project-flow-polar-chart"
                                                    codes={textCodes}
                                                    files={project.analysis}
                                                    mode="codes-per-interview"
                                                    title={t("CategoryResults.CategoryOverallFrequencyHeader")}
                                                />
                                            </ErrorBoundary>
                                            <ErrorBoundary minimal>
                                                <PolarAreaChartsPerInterview
                                                    data-testid="project-flow-polar-charts-per-interview"
                                                    codes={textCodes}
                                                    files={project.analysis}
                                                    title={t("CategoryResults.CodeFrequncyByFileHeader")} />
                                            </ErrorBoundary>
                                        </>
                                    )}


                                </div> : <div data-testid="project-flow-empty-results" className='w-full py-12 mt-4 mb-2 rounded-md bg-white p-4 flex flex-col items-center gap-8 flex-grow'>
                                    <div className='text-xl font-bold'>{t('CategoryResults.NoCodes')}</div>
                                    <div className='text-base text-gray-500 '>{t('CategoryResults.NoCodesDescription')}</div>
                                    <img src={text_coder} className=' h-96 aspect-square mt-2'></img>
                                    <button data-testid="project-flow-empty-results-coder-btn" className='border border-iavisus-100 bg-iavisus-100 text-white text-xl rounded-md px-4 py-2 '
                                        type='button'
                                        onClick={() => {
                                            /*setUploadAudioModal(<NewAudioFileUploadModal handleClose={() => { setUploadAudioModal(null) }} project={project} */
                                            navigate("/text_coder/" + project_uuid)
                                        }}
                                    >
                                        <div className='w-full flex justify-center items-center gap-2 whitespace-nowrap'>
                                            <TagIcon className='w-6 h-6' />
                                            {t("DetailProjects.Coder")}

                                        </div>
                                    </button>
                                </div>}
                            </div>
                        )}

                        {currentTab == 8 && (
                            <div data-testid="project-flow-articles-tab">
                                <ArticlesTab data-testid="project-flow-articles-component" project={project} />
                            </div>
                        )}

                        {currentTab == 9 && (
                            <div data-testid="project-flow-bibliography-tab">
                                <BibliographyTab data-testid="project-flow-bibliography-component" projectUuid={project_uuid} />
                            </div>
                        )}

                        {currentTab == 10 && (
                            <div data-testid="project-flow-video-conferences-tab">
                                {/* Rendering Video Conferences tab */}
                                <VideoConferencesList data-testid="project-flow-video-conferences-component" project={project} />
                            </div>
                        )}

                        {currentTab == 11 && (
                            <div data-testid="project-flow-questao-partida-tab">
                                <QuestaoPartidaTab 
                                    data-testid="project-flow-questao-partida-component" 
                                    projectUuid={project_uuid} 
                                    onNavigateNext={() => {
                                        markSubstepComplete(1, 1); // Mark step 1.1 as complete
                                        handleChangeTab(12); // Navigate to Research Goals
                                    }}
                                />
                            </div>
                        )}

                        {currentTab == 12 && (
                            <div data-testid="project-flow-research-goals-tab">
                                <ResearchGoalsTab 
                                    data-testid="project-flow-research-goals-component" 
                                    projectUuid={project_uuid}
                                    onNavigateNext={() => {
                                        markSubstepComplete(1, 2); // Mark step 1.2 as complete
                                        handleChangeTab(15); // Navigate to Research Hypotheses
                                    }}
                                />
                            </div>
                        )}

                        {currentTab == 15 && (
                            <div data-testid="project-flow-research-hypotheses-tab">
                                <ResearchHypothesesTab 
                                    data-testid="project-flow-research-hypotheses-component" 
                                    projectUuid={project_uuid}
                                    onNavigateNext={() => {
                                        markSubstepComplete(1, 3); // Mark step 1.3 as complete
                                        handleChangeTab(13); // Navigate to Methodology
                                    }}
                                />
                            </div>
                        )}

                        {currentTab == 13 && (
                            <div data-testid="project-flow-metodologia-tab">
                                <MetodologiaTab 
                                    data-testid="project-flow-metodologia-component" 
                                    projectUuid={project_uuid}
                                    onNavigateNext={() => {
                                        markSubstepComplete(1, 4); // Mark step 1.4 as complete
                                        handleChangeTab(14); // Navigate to Characterization
                                    }}
                                />
                            </div>
                        )}

                        {currentTab == 14 && (
                            <div data-testid="project-flow-caracterizacao-tab">
                                <CaracterizacaoTab 
                                    data-testid="project-flow-caracterizacao-component" 
                                    projectUuid={project_uuid}
                                    onNavigateNext={() => {
                                        markSubstepComplete(1, 5); // Mark step 1.5 as complete
                                        handleChangeTab(9); // Navigate to Bibliography
                                    }}
                                />
                            </div>
                        )}

                        {currentTab == 16 && (
                            <div data-testid="project-flow-questionnaire-tab">
                                <QuestionnaireTab
                                    data-testid="project-flow-questionnaire-component"
                                    projectUuid={project_uuid}
                                />
                            </div>
                        )}

                        {currentTab === 18 && (
                            <div data-testid="project-flow-tr-tab">
                                <TrAnalysesTab projectUuid={project_uuid} />
                            </div>
                        )}
                        {currentTab == 17 && (
                            <div data-testid="project-flow-be-tab">
                                <BeAnalysesTab projectUuid={project_uuid} />
                            </div>
                        )}

                    </Card>
                </div>
            </div >

            {projectSlideover && <div data-testid="project-flow-edit-modal">{projectSlideover}</div>}
            {uploadAudioModal && <div data-testid="project-flow-upload-modal">{uploadAudioModal}</div>}
        </div >
    )
}

export default ProjectFlow